<template>
  <div class="login">
    <div class="login-container">
      <!-- Logo and Brand -->
      <div class="brand-section">
        <img src="@/assets/logo.png" alt="Alliance" class="brand-logo" />
      </div>

      <!-- Login Card -->
      <v-card class="login-card" elevation="0">
        <v-card-text class="card-content">
          <div class="login-header">
            <h2 class="login-title">Welcome Back</h2>
            <p class="login-subtitle">Sign in to your account to continue</p>
          </div>

          <v-form ref="form" v-model="valid" @submit.prevent="logIn">
            <v-alert
              v-if="errorMessage"
              type="error"
              text
              dense
              border="left"
              class="mb-4"
            >
              {{ errorMessage }}
            </v-alert>

            <v-text-field
              v-model.trim="email"
              label="Email Address"
              type="email"
              autocomplete="username"
              prepend-inner-icon="mdi-email-outline"
              color="#8B0000"
              outlined
              dense
              autofocus
              hide-details="auto"
              class="mb-4 modern-input"
              :rules="emailRules"
              :disabled="loading"
              @input="errorMessage = ''"
            />

            <v-text-field
              v-model="password"
              label="Password"
              autocomplete="current-password"
              prepend-inner-icon="mdi-lock-outline"
              :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              :type="showPassword ? 'text' : 'password'"
              color="#8B0000"
              outlined
              dense
              hide-details="auto"
              class="mb-4 modern-input"
              :rules="passwordRules"
              :disabled="loading"
              @click:append="showPassword = !showPassword"
              @input="errorMessage = ''"
            />

            <div class="remember-section">
              <v-checkbox
                v-model="rememberMe"
                label="Remember email"
                color="#8B0000"
                hide-details
                class="mt-0"
              />
            </div>

            <v-btn
              type="submit"
              block
              large
              color="#8B0000"
              dark
              class="login-btn"
              :loading="loading"
              elevation="0"
            >
              Sign In
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>

      <!-- Footer -->
      <div class="login-footer">
        <p class="footer-text">© 2024 Alliance Admin Portal. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script>
import router from "@/router";

export default {
  name: "LogIn",

  data() {
    return {
      email: "",
      password: "",
      showPassword: false,
      rememberMe: false,
      loading: false,
      valid: true,
      errorMessage: "",
      emailRules: [
        (v) => !!v || "Enter your email",
        (v) => /.+@.+\..+/.test(v) || "Enter a valid email address",
      ],
      passwordRules: [(v) => !!v || "Enter your password"],
    };
  },

  mounted() {
    this.loadSavedCredentials();
  },

  watch: {
    rememberMe(newValue) {
      if (!newValue) {
        // If unchecked, clear saved email
        localStorage.removeItem("rememberedEmail");
        localStorage.removeItem("rememberMe");
      } else {
        // If checked, save email only (NOT password for security)
        localStorage.setItem("rememberMe", "true");
        if (this.email) localStorage.setItem("rememberedEmail", this.email);
      }
    },
    email(newValue) {
      if (this.rememberMe && newValue) {
        localStorage.setItem("rememberedEmail", newValue);
      }
    },
  },

  methods: {
    toast(icon, title) {
      this.$swal({
        toast: true,
        position: "top-end",
        icon,
        title,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    },

    loadSavedCredentials() {
      try {
        const savedEmail = localStorage.getItem("rememberedEmail");
        const rememberMe = localStorage.getItem("rememberMe") === "true";

        if (rememberMe && savedEmail) {
          this.email = savedEmail;
          this.rememberMe = true;
        }
        
        // Remove any old saved passwords (security cleanup)
        localStorage.removeItem("rememberedPassword");
      } catch (error) {
        console.error("Error loading saved credentials:", error);
      }
    },

    async logIn() {
      if (this.loading || !this.$refs.form.validate()) return;

      try {
        this.loading = true;
        this.errorMessage = "";

        const data = {
          email: this.email,
          password: this.password,
        };

        const response = await this.$store.dispatch("dataModules/logIn", data);

        if (response.status == 200) {
          router.push({ name: "Feedback" });
          this.toast("success", "Successfully logged in!");
        } else {
          this.errorMessage = "Wrong email or password. Please try again.";
          this.toast("error", "Wrong credentials!");
        }
      } catch (error) {
        this.errorMessage = "Login failed. Please try again.";
        this.toast("error", "Login failed. Please try again.");
        console.log("logIn: ", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$red-primary: #8B0000;
$red-dark: #5a0000;

.login {
  position: fixed;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  background: #f6f7fb;
}

.login-container {
  width: 100%;
  max-width: 460px;
  margin: auto;
}

/* Brand Section */
.brand-section {
  text-align: center;
  margin-bottom: 32px;

  .brand-logo {
    width: 120px;
    height: auto;
    margin-bottom: 16px;
    filter: drop-shadow(0 4px 12px rgba(139, 0, 0, 0.15));
  }

  .brand-title {
    font-size: 1.8rem;
    font-weight: 800;
    color: $red-primary;
    margin: 0;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .brand-subtitle {
    font-size: 0.9rem;
    color: #64748b;
    margin: 4px 0 0;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    font-weight: 600;
  }
}

/* Login Card */
.login-card {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 10px 40px rgba(139, 0, 0, 0.08) !important;
  overflow: hidden;

  .card-content {
    padding: 32px;

    @media (max-width: 600px) {
      padding: 24px;
    }
  }
}

/* Login Header */
.login-header {
  text-align: center;
  margin-bottom: 32px;

  .login-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .login-subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 8px 0 0;
  }
}

/* Form Inputs */
.modern-input {
  ::v-deep .v-input__slot {
    border-radius: 12px !important;
    background: #fbfcfe !important;
  }

  ::v-deep .v-text-field__details {
    padding-left: 12px;
  }
}

/* Remember Section */
.remember-section {
  margin-bottom: 24px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
  }

  ::v-deep .v-label {
    font-size: 0.875rem;
    color: #475569;
  }
}

/* Login Button */
.login-btn {
  height: 48px !important;
  border-radius: 12px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  background: linear-gradient(135deg, $red-primary, $red-dark) !important;
  box-shadow: 0 10px 24px rgba(139, 0, 0, 0.28) !important;
  transition: all 0.25s ease;
  margin-top: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(139, 0, 0, 0.35) !important;
  }

  &:disabled {
    opacity: 0.6;
  }
}

/* Footer */
.login-footer {
  text-align: center;
  margin-top: 24px;

  .footer-text {
    font-size: 0.75rem;
    color: #94a3b8;
    margin: 0;
  }
}

/* Responsive */
@media (max-width: 600px) {
  .login {
    padding: 16px;
  }

  .brand-section {
    margin-bottom: 24px;

    .brand-logo {
      width: 64px;
    }

    .brand-title {
      font-size: 1.5rem;
    }
  }

  .login-header {
    margin-bottom: 24px;

    .login-title {
      font-size: 1.3rem;
    }
  }
}
</style>